import { Component, OnInit, OnDestroy } from '@angular/core';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { Constants } from '../../helper/constants';

import { Global } from '../../../../app.global';
import { GlobalObservables } from '../../../../app.global.observer';
import { AlertModel } from '../../viewmodels/alert.model';
import { AlertsService } from '../../services/alerts.service';
import { LetterService } from '../../services/letter.service';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'custom-paragraphs',
  templateUrl: './custom-paragraphs.component.html',
  styleUrls: ['./custom-paragraphs.component.scss'],
})
export class CustomParagraphsComponent implements OnInit, OnDestroy {
  public constants = Constants;
  customParagraphs: Array<AlertModel> = [];
  originalCustomParagraphs: Array<AlertModel> = [];
  pagedCustomParagraphs: Array<AlertModel> = [];
  customParagraphsPerPage = 15;
  numberOfPages = 1;
  selectedPage = 1;
  pagesList = [1];
  customParagraphsSort = '';
  tooltipPlacement = 'top';
  selectedParagraphCount = 0;
  private activeInstructionsPopover: NgbPopover | null = null;
  private instructionsPopoverHoverCleanup: (() => void) | null = null;

  constructor(
    public global: Global,
    public globalObs: GlobalObservables,
    private alertsService: AlertsService,
    private letterService: LetterService
  ) {}

  ngOnInit() {
    this.setPagesList();
    this.sortCustomParagraphs(this.customParagraphsSort);
    this.getCustomParagraphs();
    this.pagedCustomParagraphs = this.customParagraphs.slice(0, this.customParagraphsPerPage);
    this.setTooltipPlacement();
    this.setSelectedCustomParagraphs();
  }

  // Close available custom paragraphs
  closeCustomParagraph() {
    this.globalObs.onCustomParagraphsShow.next(false);
  }

  getCustomParagraphs() {
    this.customParagraphs = this.global.customParagraphs;
    this.originalCustomParagraphs = this.customParagraphs;
    this.setPagesList();
  }

  setPagesList() {
    this.numberOfPages = Math.ceil(this.customParagraphs.length / this.customParagraphsPerPage);
    this.pagesList = [1];
    for (let i = 2; i <= this.numberOfPages; i++) {
      this.pagesList.push(i);
    }
  }

  setTooltipPlacement() {
    if (window.innerWidth <= this.constants.breakpoints.desktopSm) {
      this.tooltipPlacement = 'top';
    }
  }

  // The instructions popover opens on hover and, since it can now scroll, needs to
  // survive the cursor moving off the row and onto it. Three parts work together:
  //  - shrinkInstructionsPopoverGap narrows Popper's default 8px offset to 2px
  //    (the same value ng-bootstrap itself uses for its datepicker/dropdown),
  //    so there's less empty space between row and popover to cross
  //  - closeDelay (template) gives the cursor time to cross what's left of that gap
  //  - onInstructionsPopoverShown attaches hover listeners to the *whole* rendered
  //    popover element (border, padding and scrollbar included, not just the
  //    instructions text) so the bubble persists as long as the cursor is
  //    anywhere over it - including while dragging the scrollbar thumb, which
  //    sits in the popover's padding rather than in the text content itself -
  //    and also force-closes whichever popover was previously open, so two are
  //    never visible together regardless of the delay above
  shrinkInstructionsPopoverGap = (options: any) => ({
    ...options,
    modifiers: [
      ...(options.modifiers ?? []),
      { name: 'offset', options: { offset: () => [0, 2] } },
    ],
  });

  onInstructionsPopoverShown(popover: NgbPopover, triggerEl: HTMLElement) {
    if (this.activeInstructionsPopover && this.activeInstructionsPopover !== popover) {
      this.detachInstructionsPopoverHoverBridge();
      this.activeInstructionsPopover.close();
    }
    this.activeInstructionsPopover = popover;
    this.attachInstructionsPopoverHoverBridge(triggerEl);
  }

  onInstructionsPopoverHidden(popover: NgbPopover) {
    if (this.activeInstructionsPopover === popover) {
      this.detachInstructionsPopoverHoverBridge();
      this.activeInstructionsPopover = null;
    }
  }

  changePage(pageNumber) {
    const startIndex = (pageNumber - 1) * this.customParagraphsPerPage;
    const endIndex = pageNumber * this.customParagraphsPerPage;
    this.pagedCustomParagraphs = this.customParagraphs.slice(startIndex, endIndex);
    this.selectedPage = pageNumber;
  }

  sortCustomParagraphs(sortName) {
    if (sortName === 'name') {
      this.customParagraphs.sort((obj1, obj2) => {
        if (obj1.name < obj2.name) {
          return -1;
        }
        if (obj1.name > obj2.name) {
          return 1;
        }
        return 0;
      });
    }
    this.customParagraphsSort = sortName;
    this.changePage(this.selectedPage);
  }

  filterCustomParagraphs(criteria) {
    // Create a case-insensitive regular expression from the criteria
    const regex = new RegExp(criteria, 'i');

    this.customParagraphs = this.originalCustomParagraphs.filter((paragraph) => {
      if (criteria === '') {
        return true;
      }

      // Test if the paragraph name matches the regular expression
      return regex.test(paragraph.name);
    });

    this.changePage(1);
    this.sortCustomParagraphs(this.customParagraphsSort);
    this.setPagesList();
  }

  setSelectedCustomParagraphs() {
    this.alertsService.getSelectedAlerts().subscribe((result) => {
      if (result) {
        const selectedAlertsIds = [];
        result.forEach((alert) => {
          selectedAlertsIds.push(alert.id);
        });
        this.customParagraphs.forEach((paragraph) => {
          paragraph.selected = selectedAlertsIds.indexOf(paragraph.id) > -1;
        });
        this.updateSelectedParagraphCount();
      }
    });
  }

  selectCustomParagraph(paragraph) {
    // Store the current state to handle reverting in case of error
    const currentState = paragraph.selected;
    const initialCount = this.selectedParagraphCount;
    // Toggle the selected status
    paragraph.selected = !paragraph.selected;

    // Update the count of selected paragraphs
    this.updateSelectedParagraphCount();

    // Check if the selectedParagraphCount has gone from 1 to 0
    if (initialCount === 1 && this.selectedParagraphCount === 0) {
      // Specific code block to run when going from 1 to 0
      console.log('Going from 1 selected paragraph to 0');
      // Add your specific code here
    }

    // Call the service to add or remove the paragraph based on the new selected status
    this.alertsService.addRemoveParagraph(paragraph.id).subscribe(
      {
        next: (result) => {
          // Notify observers based on the existence of selected paragraphs
          if (this.existParagraphselected()) {
            this.globalObs.addCustomParagraph.next(true);
          } else {
            this.globalObs.removeCustomParagraph.next(true);
          }
        },
        error: (error) => {
          console.log(error);
          // Revert the selection state in case of an error
          paragraph.selected = currentState;
        },
      }
      // (result) => {
      //   // Notify observers based on the existence of selected paragraphs
      //   if (this.existParagraphselected()) {
      //     this.globalObs.addCustomParagraph.next(true);
      //   } else {
      //     this.globalObs.removeCustomParagraph.next(true);
      //   }
      // },
      // (error) => {
      //   console.log(error);
      //   // Revert the selection state in case of an error
      //   paragraph.selected = currentState;
      // }
    );
  }

  // Adapted method
  existParagraphselected() {
    return this.pagedCustomParagraphs.some((paragraph) => paragraph.selected);
  }

  updateSelectedParagraphCount() {
    this.selectedParagraphCount = this.getNumberOfParagraphsSelected();
  }

  getNumberOfParagraphsSelected() {
    let parSelected = 0;
    this.customParagraphs.forEach((paragraph) => {
      if (paragraph.selected) {
        parSelected += 1;
      }
    });
    return parSelected;
  }

  ngOnDestroy() {
    this.detachInstructionsPopoverHoverBridge();
    // clear selected custom Paragraphs on leaving the component
    this.customParagraphs.forEach((paragraph) => {
      paragraph.selected = false;
    });
  }

  private attachInstructionsPopoverHoverBridge(triggerEl: HTMLElement) {
    this.detachInstructionsPopoverHoverBridge();
    const popoverId = triggerEl.getAttribute('aria-describedby');
    const popoverEl = popoverId ? document.getElementById(popoverId) : null;
    if (!popoverEl) {
      return;
    }
    const keepOpen = () => triggerEl.dispatchEvent(new MouseEvent('mouseenter'));
    const scheduleClose = () => triggerEl.dispatchEvent(new MouseEvent('mouseleave'));
    popoverEl.addEventListener('mouseenter', keepOpen);
    popoverEl.addEventListener('mouseleave', scheduleClose);
    this.instructionsPopoverHoverCleanup = () => {
      popoverEl.removeEventListener('mouseenter', keepOpen);
      popoverEl.removeEventListener('mouseleave', scheduleClose);
    };
  }

  private detachInstructionsPopoverHoverBridge() {
    if (this.instructionsPopoverHoverCleanup) {
      this.instructionsPopoverHoverCleanup();
      this.instructionsPopoverHoverCleanup = null;
    }
  }
}
